import { useCallback, useEffect, useMemo } from 'react';

import { AgentIdStatus } from '../components/types/agent-id';

import { useCheckAgentIdAvailability } from './useSpecializedAgents';

type AgentIdFieldStatus = {
  message: string;
  color: 'fg.secondary' | 'fg.success' | 'fg.error';
};

type UseAgentIdReturn = {
  isCheckingAvailability: boolean;
  availabilityStatus: AgentIdStatus | undefined;
  idFieldStatus: AgentIdFieldStatus | null;
  isSaveDisabled: boolean;
};

export function useAgentId(
  instanceId: string | null,
  currentAgentId: string,
  isEditing: boolean,
  originalAgentId?: string
): UseAgentIdReturn {
  const {
    mutate: checkAgentIdAvailability,
    isPending: isCheckingAgentIdAvailability,
    data: availabilityData,
  } = useCheckAgentIdAvailability();

  const checkAvailability = useCallback(
    (agentId: string) => {
      if (!instanceId || !agentId) {
        return;
      }

      if (isEditing && !originalAgentId) {
        return;
      }

      if (isEditing && agentId === originalAgentId) {
        return;
      }

      checkAgentIdAvailability({ agentId, instanceId });
    },
    [instanceId, checkAgentIdAvailability, isEditing, originalAgentId]
  );

  useEffect(() => {
    const trimmedId = currentAgentId?.trim();
    if (!trimmedId) return;

    const timeoutId = setTimeout(() => {
      checkAvailability(trimmedId);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [currentAgentId, checkAvailability]);

  const idFieldStatus = useMemo((): AgentIdFieldStatus | null => {
    if (isCheckingAgentIdAvailability) {
      return { message: 'Checking availability...', color: 'fg.secondary' };
    }

    if (availabilityData?.lastCheckedId === currentAgentId) {
      if (availabilityData.available === true) {
        return { message: 'Available', color: 'fg.success' };
      }
      if (availabilityData.available === false) {
        return { message: 'Not available', color: 'fg.error' };
      }
    }

    return null;
  }, [isCheckingAgentIdAvailability, availabilityData, currentAgentId]);

  const isSaveDisabled = useMemo(
    () =>
      isCheckingAgentIdAvailability ||
      !currentAgentId ||
      (availabilityData?.lastCheckedId === currentAgentId && availabilityData.available === false),
    [isCheckingAgentIdAvailability, currentAgentId, availabilityData]
  );

  return {
    isCheckingAvailability: isCheckingAgentIdAvailability,
    availabilityStatus: availabilityData,
    idFieldStatus,
    isSaveDisabled,
  };
}
